import { cn } from '@e-kyc/utils';
import { useController, type FieldValues, type UseControllerProps } from 'react-hook-form';

type FormCheckboxProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  React.ComponentProps<'input'> & {
    children: JSX.Element | string;
    required?: boolean;
  };

export function Checkbox<TFieldValues extends FieldValues = FieldValues>({
  id,
  checked,
  children,
  disabled = false,
  name,
  required,
  className,
  ...props
}: FormCheckboxProps<TFieldValues>) {
  const {
    field: { ...field },
  } = useController({ ...props, name, rules: { required, ...props.rules } });
  return (
    <div className='flex items-center gap-2'>
      <label htmlFor={id} className='inline-flex cursor-pointer items-center space-x-2'>
        <input
          {...field}
          type='checkbox'
          id={id}
          checked={checked}
          disabled={disabled}
          className='peer hidden'
          {...props}
        />
        <div
          className={cn(
            'flex min-h-[18px] min-w-[18px] items-center justify-center rounded-xs border-2',
            disabled
              ? 'border-gray-300 peer-checked:bg-gray-300 checked:peer-checked:bg-gray-300'
              : 'peer-checked:bg-primary peer-checked:border-primary border-gray-400',
            className,
          )}
        >
          <svg
            className='w-6 text-white'
            fill='none'
            stroke='currentColor'
            strokeWidth='3'
            viewBox='0 0 24 24'
          >
            <path strokeLinecap='square' strokeLinejoin='round' d='M5 13l4 4L19 7' />
          </svg>
        </div>
        <div className={cn(['text-body2 cursor-pointer', disabled && 'text-body2 !text-gray-300'])}>
          {children}
        </div>
      </label>
    </div>
  );
}
